<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'

interface SubmenuItem {
  name: string
  to?: RouteLocationRaw
  href?: string
  icon: string
  description?: string
  label?: string
  items?: SubmenuItem[]
}

interface MenuItem {
  name: string
  href?: string
  to?: RouteLocationRaw
  icon: string
  submenu?: SubmenuItem[]
  label?: string
}

defineProps<{
  navigation: MenuItem[]
  hideToggle?: boolean
}>()

const model = defineModel<boolean>({ default: false })

const router = useRouter()
const route = useRoute()

// Reactive state
const isOpen = ref(false)
const openSubmenu = ref<string | null>(null)
const openNestedSubmenu = ref<string | null>(null)
let scrollPosition = 0

// Sync with v-model
watch(model, val => {
  isOpen.value = val
})

watch(isOpen, val => {
  model.value = val

  // Prevent body scroll when menu is open
  if (val) {
    // Save current scroll position
    scrollPosition = window.scrollY
    document.body.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollPosition}px`
    document.body.style.width = '100%'
  }
  else {
    // Restore scroll position
    document.body.style.overflow = ''
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.width = ''
    window.scrollTo(0, scrollPosition)
  }
})

// Mobile menu item click handler
function handleMobileItemClick(item: MenuItem) {
  if (item.submenu) {
    const wasOpen = openSubmenu.value === item.name

    toggleSubmenu(item.name)

    // If the submenu was already open and we're closing it, navigate to the item's URL
    if (wasOpen && (item.href || item.to)) {
      setTimeout(() => {
        router.push(item.to || item.href!)
        closeMobileMenu()
      }, 300)
    }

    return
  }

  // For items without submenu, navigate immediately
  if (item.href || item.to) {
    router.push(item.to || item.href!)
    closeMobileMenu()
  }
}

// Function to close the mobile menu
function closeMobileMenu() {
  isOpen.value = false
  openSubmenu.value = null
  openNestedSubmenu.value = null
}

// Toggle submenu
function toggleSubmenu(menuName: string) {
  openSubmenu.value = openSubmenu.value === menuName ? null : menuName

  // Close nested submenu when parent submenu changes
  if (openSubmenu.value !== menuName)
    openNestedSubmenu.value = null
}

// Toggle nested submenu
function toggleNestedSubmenu(menuName: string) {
  openNestedSubmenu.value = openNestedSubmenu.value === menuName ? null : menuName
}

// Helper active state
function isActive(href?: string) {
  if (!href)
    return false
  if (href === '/')
    return route.path === '/'

  return route.path === href || route.path.startsWith(`${href}/`)
}

// Watch for route changes to close the menu
watch(
  () => route.path,
  () => {
    closeMobileMenu()
  },
)

// Close menu when pressing Escape key
onMounted(() => {
  const handleEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen.value)
      closeMobileMenu()
  }

  window.addEventListener('keydown', handleEscape)

  onUnmounted(() => {
    window.removeEventListener('keydown', handleEscape)
  })
})

// Expose methods
defineExpose({
  closeMobileMenu,
  isOpen,
})
</script>

<template>
  <div class="mobile-menu-wrapper">
    <!-- Mobile menu toggle button -->
    <button
      v-if="!hideToggle"
      class="mobile-menu-toggle"
      :aria-label="isOpen ? 'Close menu' : 'Open menu'"
      @click="isOpen = !isOpen"
    >
      <div class="hamburger-menu">
        <span
          class="bar-top"
          :class="{ open: isOpen }"
        />
        <span
          class="bar-middle"
          :class="{ open: isOpen }"
        />
        <span
          class="bar-bottom"
          :class="{ open: isOpen }"
        />
      </div>
    </button>

    <!-- Mobile menu dropdown -->
    <div
      class="mobile-menu-container"
      :class="{ open: isOpen }"
    >
      <div class="mobile-menu-content">
        <div
          v-for="item in navigation"
          :key="item.name"
          class="menu-item-wrapper"
        >
          <!-- Mobile menu item -->
          <button
            class="menu-item"
            :class="{
              'menu-item-active': isActive(item.href) && !item.submenu,
              'menu-item-active-submenu': isActive(item.href) && item.submenu,
            }"
            @click="handleMobileItemClick(item)"
          >
            <span class="menu-item-content">
              <VIcon
                :icon="item.icon"
                class="menu-item-icon"
              />
              {{ item.label || item.name }}
            </span>
            <VIcon
              v-if="item.submenu"
              icon="bx-chevron-down"
              class="submenu-chevron"
              :class="{ 'submenu-chevron-open': openSubmenu === item.name }"
            />
          </button>

          <!-- Mobile submenu with animation -->
          <div
            v-if="item.submenu"
            class="submenu-container"
            :class="{
              'submenu-open': openSubmenu === item.name,
              'submenu-closed': openSubmenu !== item.name,
            }"
          >
            <div class="submenu-content">
              <template
                v-for="subItem in item.submenu"
                :key="subItem.name"
              >
                <!-- Submenu with nested items -->
                <div
                  v-if="subItem.items"
                  class="nested-submenu-wrapper"
                >
                  <button
                    class="submenu-item submenu-item-parent"
                    @click="toggleNestedSubmenu(subItem.name)"
                  >
                    <VIcon
                      :icon="subItem.icon"
                      class="submenu-item-icon"
                    />
                    <div class="submenu-item-text">
                      <p class="submenu-item-title">
                        {{ subItem.label || subItem.name }}
                      </p>
                    </div>
                    <VIcon
                      icon="bx-chevron-down"
                      class="nested-chevron"
                      :class="{ 'nested-chevron-open': openNestedSubmenu === subItem.name }"
                    />
                  </button>

                  <!-- Nested submenu items -->
                  <div
                    class="nested-submenu-container"
                    :class="{
                      'nested-submenu-open': openNestedSubmenu === subItem.name,
                      'nested-submenu-closed': openNestedSubmenu !== subItem.name,
                    }"
                  >
                    <NuxtLink
                      v-for="nestedItem in subItem.items"
                      :key="nestedItem.name"
                      class="nested-submenu-item"
                      :class="{
                        'submenu-item-active': isActive(nestedItem.href),
                      }"
                      :to="nestedItem.to"
                      @click="closeMobileMenu"
                    >
                      <VIcon
                        :icon="nestedItem.icon"
                        class="nested-submenu-item-icon"
                      />
                      <span class="nested-submenu-item-label">
                        {{ nestedItem.label || nestedItem.name }}
                      </span>
                    </NuxtLink>
                  </div>
                </div>

                <!-- Regular submenu item without nested items -->
                <NuxtLink
                  v-else
                  class="submenu-item"
                  :class="{
                    'submenu-item-active': isActive(subItem.href),
                  }"
                  :to="subItem.to"
                  @click="closeMobileMenu"
                >
                  <VIcon
                    :icon="subItem.icon"
                    class="submenu-item-icon"
                  />
                  <div class="submenu-item-text">
                    <p class="submenu-item-title">
                      {{ subItem.label || subItem.name }}
                    </p>
                    <p
                      v-if="subItem.description"
                      class="submenu-item-description"
                    >
                      {{ subItem.description }}
                    </p>
                  </div>
                </NuxtLink>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Overlay to capture clicks outside the menu when menu is open -->
    <div
      v-if="isOpen"
      class="mobile-menu-overlay"
      @click="closeMobileMenu"
    />
  </div>
</template>

<style scoped lang="scss">
/* Wrapper to contain all elements */
.mobile-menu-wrapper {
  position: relative;
  display: inline-block;
}

/* Hamburger menu styles */
.mobile-menu-toggle {
  padding: 0.5rem;
}

.hamburger-menu {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  block-size: 20px;
  color: rgb(var(--v-theme-primary));
  inline-size: 24px;
}

.hamburger-menu span {
  display: block;
  background-color: currentcolor;
  block-size: 2px;
  inline-size: 100%;
  transform-origin: center;
  transition: all 0.3s ease-in-out;
}

/* Top bar animation */
.bar-top.open {
  transform: translateY(9px) rotate(45deg);
}

/* Middle bar animation */
.bar-middle.open {
  opacity: 0;
  transform: scaleX(0);
}

/* Bottom bar animation */
.bar-bottom.open {
  transform: translateY(-9px) rotate(-45deg);
}

/* Mobile menu dropdown animation */
.mobile-menu-container {
  position: fixed;
  z-index: 1000;
  overflow: hidden;
  border: 2px solid rgba(var(--v-theme-surface), 8%);
  border-radius: 0.375rem;
  backdrop-filter: saturate(100%) blur(6px);
  background-color: rgb(var(--v-theme-surface));
  inset-block-start: 62px;
  inset-inline: 0;
  max-block-size: 0;
  opacity: 0;
  transform: translateY(0);
  transition: all 0.3s ease-in-out;
  visibility: hidden;
}

.mobile-menu-container.open {
  overflow: hidden auto;
  max-block-size: calc(100vh - 80px);
  opacity: 1;
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
  overscroll-behavior: contain; /* Prevent scroll chaining to body */
  touch-action: pan-y; /* Only allow vertical scrolling */
  transform: translateY(0);
  visibility: visible;
}

.mobile-menu-content {
  padding: 0.5rem;
  font-family: "Peachy Kee JF", "Public Sans", sans-serif;
}

.menu-item-wrapper {
  margin-block-end: 0.25rem;
}

.menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  border: none;
  border-radius: 0.375rem;
  background: transparent;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  cursor: pointer;
  font-family: "Peachy Kee JF", "Public Sans", sans-serif;
  font-size: 1.125rem;
  font-weight: 500;
  inline-size: 100%;
  text-align: start;
  transition: all 0.2s ease;
}

.menu-item:hover {
  background-color: rgba(var(--v-theme-primary), 0.08);
  color: rgb(var(--v-theme-primary));
}

.menu-item-active {
  background-color: rgba(var(--v-theme-primary), 0.12);
  color: rgb(var(--v-theme-primary));
  font-weight: 600;
}

.menu-item-active-submenu {
  background-color: rgba(var(--v-theme-primary), 0.06);
}

.menu-item-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.menu-item-icon {
  color: rgb(var(--v-theme-primary));
  font-size: 1.25rem;
}

.submenu-chevron {
  color: rgb(var(--v-theme-primary));
  font-size: 1.25rem;
  transition: transform 0.3s ease;
}

.submenu-chevron-open {
  transform: rotate(180deg);
}

/* Submenu animation styles */
.submenu-container {
  overflow: hidden;
  max-block-size: 0;
  opacity: 0;
  transition: all 0.3s ease-in-out;
}

.submenu-open {
  margin-block: 0.25rem 0.5rem;
  max-block-size: 2000px;
  opacity: 1;
}

.submenu-closed {
  max-block-size: 0;
  opacity: 0;
}

.submenu-content {
  border-inline-start: 2px solid rgb(var(--v-theme-primary));
  margin-inline-start: 0.75rem;
  padding-inline-start: 0.75rem;
}

.submenu-item {
  display: flex;
  align-items: flex-start;
  border-radius: 0.375rem;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  margin-block-end: 0.125rem;
  padding-block: 0.375rem;
  padding-inline: 0.5rem;
  text-decoration: none;
  transition: all 0.2s ease;
}

.submenu-item:hover {
  background-color: rgba(var(--v-theme-primary), 0.08);
  color: rgb(var(--v-theme-primary));
}

.submenu-item-active {
  background-color: rgba(var(--v-theme-primary), 0.12);
  color: rgb(var(--v-theme-primary));
  font-weight: 500;
}

.submenu-item-icon {
  flex-shrink: 0;
  color: rgb(var(--v-theme-primary));
  font-size: 1rem;
  margin-block-start: 0.125rem;
}

.submenu-item-text {
  flex: 1;
  margin-inline-start: 0.5rem;
}

.submenu-item-title {
  margin: 0;
  font-family: "Peachy Kee JF", "Public Sans", sans-serif;
  font-size: 1.2rem;
  font-weight: 500;
  line-height: 1.3;
}

.submenu-item-description {
  color: rgba(var(--v-theme-on-surface), 0.6);
  font-family: "Peachy Kee JF", "Public Sans", sans-serif;
  font-size: 1rem;
  line-height: 1.2;
  margin-block: 0.125rem 0 0;
}

/* Nested submenu styles */
.nested-submenu-wrapper {
  margin-block-end: 0.125rem;
}

.submenu-item-parent {
  justify-content: space-between;
  inline-size: 100%;
  text-align: start;
}

.submenu-item-parent .submenu-item-text {
  text-align: start;
}

.nested-chevron {
  color: rgb(var(--v-theme-primary));
  font-size: 1rem;
  margin-block-start: 5px;
  margin-inline-start: auto;
  transition: transform 0.3s ease;
}

.nested-chevron-open {
  transform: rotate(180deg);
}

.nested-submenu-container {
  overflow: hidden;
  max-block-size: 0;
  opacity: 0;
  transition: all 0.3s ease-in-out;
}

.nested-submenu-open {
  margin-block: 0.25rem 0;
  max-block-size: 1500px;
  opacity: 1;
}

.nested-submenu-closed {
  max-block-size: 0;
  opacity: 0;
}

.nested-submenu-item {
  display: flex;
  align-items: center;
  border-radius: 0.375rem;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  margin-block-end: 0.125rem;
  margin-inline-start: 1rem;
  padding-block: 0.3rem;
  padding-inline: 0.5rem;
  text-decoration: none;
  transition: all 0.2s ease;
}

.nested-submenu-item:hover {
  background-color: rgba(var(--v-theme-primary), 0.08);
  color: rgb(var(--v-theme-primary));
}

.nested-submenu-item-icon {
  flex-shrink: 0;
  color: rgb(var(--v-theme-primary));
  font-size: 0.9rem;
  margin-inline-end: 0.5rem;
}

.nested-submenu-item-label {
  font-family: "Peachy Kee JF", "Public Sans", sans-serif;
  font-size: 1rem;
  font-weight: 400;
}

/* Overlay */
.mobile-menu-overlay {
  position: fixed;
  z-index: 999;
  background-color: transparent;
  inset: 0;
}

/* Smooth scrolling for mobile menu */
.mobile-menu-container::-webkit-scrollbar {
  inline-size: 6px;
}

.mobile-menu-container::-webkit-scrollbar-track {
  background: transparent;
}

.mobile-menu-container::-webkit-scrollbar-thumb {
  border-radius: 3px;
  background: rgba(var(--v-theme-primary), 0.3);
}

.mobile-menu-container::-webkit-scrollbar-thumb:hover {
  background: rgba(var(--v-theme-primary), 0.5);
}
</style>
